import { createClient } from '@supabase/supabase-js'

const supabaseUrl = (import.meta as any).env.VITE_SUPABASE_URL
const supabaseAnonKey = (import.meta as any).env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn('Supabase配置未找到，请检查环境变量 VITE_SUPABASE_URL 和 VITE_SUPABASE_ANON_KEY')
}

export const supabase = createClient(supabaseUrl || '', supabaseAnonKey || '')

// 认证相关服务
export const authService = {
  async signIn(email: string, password: string) {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    return { data, error }
  },

  async signUp(email: string, password: string) {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
    })
    return { data, error }
  },

  async signOut() {
    const { error } = await supabase.auth.signOut()
    return { error }
  },

  async getCurrentUser() {
    const { data: { user }, error } = await supabase.auth.getUser()
    return { user, error }
  },

  /**
   * Get user from local session cache (NO network request).
   * Use this for high-frequency calls like progress lookups.
   * Use getCurrentUser() instead for sensitive auth operations.
   */
  async getSessionUser() {
    const { data: { session } } = await supabase.auth.getSession()
    return { user: session?.user ?? null }
  },

  onAuthStateChange(callback: (event: string, session: any) => void) {
    return supabase.auth.onAuthStateChange(callback)
  },

  // 密码重置功能
  async resetPassword(email: string) {
    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/?reset-password=true`,
    })
    return { data, error }
  },

  // 更新用户密码
  async updatePassword(newPassword: string) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    })
    return { data, error }
  },

  // 获取当前会话信息
  async getCurrentSession() {
    const { data: { session }, error } = await supabase.auth.getSession()
    return { session, error }
  },

  // 使用OAuth令牌重置密码
  async resetPasswordWithToken(newPassword: string) {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    })
    return { data, error }
  },

  // 使用 Google OAuth 登录
  async signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    })
    return { data, error }
  },

  // 使用 Google ID Token 登录 (用于 One Tap)
  async signInWithGoogleIdToken(idToken: string, nonce?: string) {
    const { data, error } = await supabase.auth.signInWithIdToken({
      provider: 'google',
      token: idToken,
      ...(nonce ? { nonce } : {}),
    })
    return { data, error }
  },
}

// 用户相关服务
export const userService = {
  async getUserProfile(userId: string) {
    const { data, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', userId)
      .single()
    return { data, error }
  },

  async updateUserProfile(userId: string, updates: any) {
    const { data, error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', userId)
      .select()
      .single()
    return { data, error }
  },
}

// 拼音相关服务
export const pinyinService = {
  async getPinyinList(type?: 'shengmu' | 'yunmu' | 'zhengti') {
    let query = supabase.from('pinyin').select('*')
    if (type) {
      query = query.eq('type', type)
    }
    const { data, error } = await query
    return { data, error }
  },

  async getPinyinById(id: string) {
    const { data, error } = await supabase
      .from('pinyin')
      .select('*')
      .eq('id', id)
      .single()
    return { data, error }
  },
}

// 对话相关服务
export const conversationService = {
  async getConversations(userId: string) {
    const { data, error } = await supabase
      .from('conversations')
      .select(`
        *,
        messages (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
    return { data, error }
  },

  async createConversation(conversation: any) {
    const { data, error } = await supabase
      .from('conversations')
      .insert([conversation])
      .select()
      .single()
    return { data, error }
  },

  async addMessage(message: any) {
    const { data, error } = await supabase
      .from('messages')
      .insert([message])
      .select()
      .single()
    return { data, error }
  },
}

// 阅读材料服务
export const readingService = {
  async getReadingMaterials(level?: string, category?: string) {
    let query = supabase.from('reading_materials').select('*')
    if (level) {
      query = query.eq('level', level)
    }
    if (category) {
      query = query.eq('category', category)
    }
    const { data, error } = await query
    return { data, error }
  },

  async getReadingMaterialById(id: string) {
    const { data, error } = await supabase
      .from('reading_materials')
      .select('*')
      .eq('id', id)
      .single()
    return { data, error }
  },
}

// 学习进度服务
export const progressService = {
  async getUserProgress(userId: string) {
    const { data, error } = await supabase
      .from('learning_progress')
      .select('*')
      .eq('user_id', userId)
      .single()
    return { data, error }
  },

  async updateUserProgress(userId: string, updates: any) {
    const { data, error } = await supabase
      .from('learning_progress')
      .upsert([{ user_id: userId, ...updates }])
      .select()
      .single()
    return { data, error }
  },
}

// 成语相关服务
export const chengyuService = {
  // 获取成语列表
  async getIdioms(stage?: string, limit?: number) {
    let query = supabase.from('idioms').select('*')

    if (stage) {
      query = query.eq('stage', stage)
    }

    // 如果没有指定 limit，默认获取 1000 条
    const finalLimit = limit || 1000
    query = query.limit(finalLimit)

    const { data, error } = await query
    return { data, error }
  },

  // 根据阶段获取成语列表（返回 IdiomSearchResult 格式）
  async getIdiomsByStage(stage: string, params?: { limit?: number; page?: number }) {
    const limit = params?.limit || 1000
    const page = params?.page || 1
    const offset = (page - 1) * limit

    let query = supabase.from('idioms').select('*', { count: 'exact' })

    if (stage) {
      query = query.eq('stage', stage)
    }

    const { data, error, count } = await query.range(offset, offset + limit - 1)

    return {
      idioms: data || [],
      total: count || 0,
      page,
      limit,
      totalPages: Math.ceil((count || 0) / limit),
      error
    }
  },

  // 根据ID获取成语详情
  async getIdiomById(word: string) {
    const { data, error } = await supabase
      .from('idioms')
      .select('*')
      .eq('word', word)
      .single()
    return { data, error }
  },

  // 搜索成语
  async searchIdioms(keyword: string, stage?: string, limit?: number) {
    // Escape special characters that could break PostgREST .or() query structure
    const sanitized = keyword.replace(/[,()\\]/g, '');
    if (!sanitized.trim()) return { data: [], error: null };

    let query = supabase
      .from('idioms')
      .select('*')
      .or(`word.ilike.%${sanitized}%,pinyin.ilike.%${sanitized}%,explanation.ilike.%${sanitized}%`)

    if (stage) {
      query = query.eq('stage', stage)
    }

    if (limit) {
      query = query.limit(limit)
    }

    const { data, error } = await query
    return { data, error }
  },

  // 获取随机成语
  async getRandomIdioms(stage?: string, count: number = 5) {
    // 1. First get the total count for the stage
    let countQuery = supabase
      .from('idioms')
      .select('*', { count: 'exact', head: true })

    if (stage) {
      countQuery = countQuery.eq('stage', stage)
    }

    const { count: totalCount, error: countError } = await countQuery

    if (countError || totalCount === null || totalCount === 0) {
      return { data: [], error: countError }
    }

    // 2. Generate a random offset
    // Ensure we can fetch 'count' items from the offset
    const maxOffset = Math.max(0, totalCount - count)
    const randomOffset = Math.floor(Math.random() * (maxOffset + 1))

    // 3. Fetch data using the random offset
    let query = supabase
      .from('idioms')
      .select('*')
      .range(randomOffset, randomOffset + count - 1)

    if (stage) {
      query = query.eq('stage', stage)
    }

    const { data, error } = await query

    if (error || !data) {
      return { data: [], error }
    }

    // 4. Shuffle the result (in case we fetched a contiguous block)
    const shuffled = data.sort(() => 0.5 - Math.random())

    return { data: shuffled, error: null }
  },

  // 获取用户成语学习进度
  async getUserChengyuProgress(userId: string) {
    const { data, error } = await supabase
      .from('user_chengyu_progress')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle()
    return { data, error }
  },

  // 更新用户成语学习进度
  async updateUserChengyuProgress(userId: string, progress: any) {
    const { data, error } = await supabase
      .from('user_chengyu_progress')
      .upsert([{ user_id: userId, ...progress }])
      .select()
      .single()
    return { data, error }
  },

  // 获取用户成语学习记录
  async getUserChengyuRecords(userId: string, stage?: string, limit?: number) {
    let query = supabase
      .from('user_chengyu_records')
      .select(`
        *,
        idioms (*)
      `)
      .eq('user_id', userId)
      .order('created_at', { ascending: false })

    if (stage) {
      query = query.eq('stage', stage)
    }

    if (limit) {
      query = query.limit(limit)
    }

    const { data, error } = await query
    return { data, error }
  },

  // 记录用户成语学习活动
  async recordChengyuActivity(record: any) {
    const { data, error } = await supabase
      .from('user_chengyu_records')
      .insert([record])
      .select()
      .single()
    return { data, error }
  },

  // 获取成语的英文翻译
  async getIdiomTranslation(word: string) {
    const { data, error } = await supabase
      .from('idiom_translations')
      .select('*')
      .eq('word', word)
      .maybeSingle()
    return { data, error }
  },

  // 保存成语的英文翻译
  async saveIdiomTranslation(translation: any) {
    const { data, error } = await supabase
      .from('idiom_translations')
      .upsert([translation], { onConflict: 'word' })
      .select()
      .single()
    return { data, error }
  }
}